import { jwtDecode } from 'jwt-decode';
import { create } from 'zustand';

import {
    deleteKeyCloakTokens,
    getKeyCloakTokens,
    saveKeyCloakTokens,
} from 'kerberus-components/src/Utils/device/renkiInvoke';

import { OfflineToken, SsoToken } from '../Types/Auth';

interface DeviceFlowResponse {
    device_code: string;
    expires_in: number;
    interval: number;
    user_code: string;
    verification_uri: string;
    verification_uri_complete: string;
}

interface TokenResponse {
    access_token: string;
    error?: string;
    error_description?: string;
    error_uri?: string;
    expires_in: number;
    id_token: string;
    refresh_expires_in: number;
    refresh_token: string;
    scope: string;
    session_state: string;
    token_type: string;
}

interface AuthState {
    authenticated: boolean;
    deviceFlow: DeviceFlowResponse | null;
    initPending: boolean;
    parsedToken: SsoToken | null;
    refresh: string;
    token: string;
    tokenPromise: Promise<string> | null;
}

export interface UseAuthStore extends AuthState {
    initialize: () => Promise<void>;
    logout: () => void;
    updateToken: () => Promise<string>;
}

const cloakHost = process.env.EXPO_PUBLIC_CLOAK_HOST!;
const cloakRealm = process.env.EXPO_PUBLIC_CLOAK_REALM!;
const cloakClient = process.env.EXPO_PUBLIC_CLOAK_CLIENT!;

const defaultAuthStore: AuthState = {
    authenticated: false,
    deviceFlow: null,
    initPending: false,
    parsedToken: null,
    refresh: '',
    token: '',
    tokenPromise: null,
};

// Should maybe check time skew like keycloak does
function validOfflineToken(token: string): boolean {
    if (!token) {
        return false;
    }
    try {
        jwtDecode<OfflineToken>(token);
        return true;
    } catch (error) {
        console.error(error);
    }
    return false;
}

function isTokenExpired(token: SsoToken) {
    const expiresIn = token.exp - Math.ceil(new Date().getTime() / 1000) - 10; // 10 seconds buffer
    return expiresIn < 0;
}

async function refreshToken(refresh: string, set: (state: Partial<AuthState>) => void): Promise<string> {
    console.log('trying refresh?');
    let newState: Partial<AuthState> = { tokenPromise: null };
    try {
        const response = await fetch(`${cloakHost}/realms/${cloakRealm}/protocol/openid-connect/token`, {
            body: new URLSearchParams({
                client_id: cloakClient,
                grant_type: 'refresh_token',
                refresh_token: refresh,
            }),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            method: 'POST',
        });

        if (!response.ok) {
            throw new Error('Failed to refresh token');
        }

        const data = (await response.json()) as TokenResponse;
        if (data.error) {
            if (data.error === 'invalid_grant') {
                throw new Error('Invalid refresh token');
            }
            throw new Error(`Failed to refresh token: ${data.error}`);
        }

        saveKeyCloakTokens({
            kcIdToken: data.id_token,
            kcRefreshToken: data.refresh_token,
            kcToken: data.access_token,
        }).catch(console.error);

        const parsedToken = jwtDecode<SsoToken>(data.access_token);

        newState = {
            authenticated: true,
            parsedToken,
            refresh: data.refresh_token,
            token: data.access_token,
            tokenPromise: null,
        };

        return data.access_token;
    } finally {
        set(newState);
    }
}

export const useDeviceFlowAuthStore = create<UseAuthStore>()((set, get) => ({
    ...defaultAuthStore,
    initialize: async () => {
        if (get().initPending) {
            return;
        }
        set({ initPending: true });
        try {
            try {
                const tokens = await getKeyCloakTokens();
                const parsedAccess = jwtDecode<SsoToken>(tokens.accessToken);
                if (!isTokenExpired(parsedAccess)) {
                    set({
                        authenticated: true,
                        parsedToken: parsedAccess,
                        refresh: tokens.refreshToken,
                        token: tokens.accessToken,
                    });
                    return;
                } else if (validOfflineToken(tokens.refreshToken)) {
                    set({ refresh: tokens.refreshToken });
                    await get().updateToken();
                    return;
                }
            } catch (error) {
                console.error('Failed to get stored tokens:', error);
                try {
                    await deleteKeyCloakTokens();
                } catch (error) {
                    console.error('Failed to delete stored tokens:', error);
                }
            }

            console.log('initializing device flow');
            const response = await fetch(`${cloakHost}/realms/${cloakRealm}/protocol/openid-connect/auth/device`, {
                body: new URLSearchParams({
                    client_id: cloakClient,
                    scope: 'openid offline_access',
                }),
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                method: 'POST',
            });

            if (!response.ok) {
                throw new Error('Failed to initiate device flow');
            }

            const deviceFlow = (await response.json()) as DeviceFlowResponse;

            set({ deviceFlow });

            const pollInterval = (deviceFlow.interval || 5) * 1000;
            let currentPollInterval = pollInterval;

            const newTokens = await new Promise<TokenResponse>((resolve, reject) => {
                async function handlePoll() {
                    try {
                        const response = await fetch(
                            `${cloakHost}/realms/${cloakRealm}/protocol/openid-connect/token`,
                            {
                                body: new URLSearchParams({
                                    client_id: cloakClient,
                                    device_code: deviceFlow.device_code,
                                    grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
                                }),
                                headers: {
                                    'Content-Type': 'application/x-www-form-urlencoded',
                                },
                                method: 'POST',
                            },
                        );

                        const data = (await response.json()) as TokenResponse;

                        if (data.error) {
                            if (data.error === 'authorization_pending') {
                                return;
                            }

                            if (data.error === 'slow_down') {
                                clearInterval(intervalId);
                                currentPollInterval += 5000;
                                console.log(
                                    `Slowing down polling to ${(currentPollInterval / 1000).toString()} seconds`,
                                );
                                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                intervalId = setInterval(handlePoll, currentPollInterval);
                                return;
                            }

                            clearInterval(intervalId);
                            reject(new Error(`Authentication failed: ${data.error}`));
                            return;
                        }

                        clearInterval(intervalId);
                        resolve(data);
                    } catch (error) {
                        clearInterval(intervalId);
                        reject(error as Error);
                    }
                }

                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                let intervalId = setInterval(handlePoll, pollInterval);
            });

            console.log('new tokens', newTokens);
            saveKeyCloakTokens({
                kcIdToken: newTokens.id_token,
                kcRefreshToken: newTokens.refresh_token,
                kcToken: newTokens.access_token,
            }).catch(console.error);
            const parsedToken = jwtDecode<SsoToken>(newTokens.access_token);
            set({
                authenticated: true,
                parsedToken,
                refresh: newTokens.refresh_token,
                token: newTokens.access_token,
            });
        } catch (error) {
            console.error('Failed to initialize auth:', error);
            set({ authenticated: false });
        } finally {
            set({ initPending: false });
        }
    },
    logout: () => {
        set({ authenticated: false, refresh: '', token: '' });
        deleteKeyCloakTokens().catch(console.error);
    },
    updateToken: async () => {
        const { parsedToken, refresh, token, tokenPromise } = get();

        if (parsedToken && !isTokenExpired(parsedToken)) {
            return token;
        }

        if (!validOfflineToken(refresh)) {
            set({ authenticated: false });
            throw new Error('Refresh token is invalid or missing');
        }

        if (tokenPromise) {
            return tokenPromise;
        }

        const newTokenPromise = refreshToken(refresh, (token) => set(token));
        set({ tokenPromise: newTokenPromise });
        return newTokenPromise;
    },
}));
